/**
 * Dấu hiệu thương hiệu — bốn hình, mỗi giao diện một hình.
 *
 * Hình vẽ là một phần của khác biệt nhận diện chứ không phải chỗ trang trí: một dấu
 * hiệu bo tròn hữu cơ và một tia chớp góc nhọn nói hai điều khác nhau trước khi người
 * xem kịp đọc chữ. Cả bốn đều lấy màu từ `--brand` nên không hình nào tự đặt màu.
 */

import type { JSX } from 'react';
import type { MarkId } from '../themes/types';

interface Props {
  mark: MarkId;
  size?: number;
  className?: string;
}

export function BrandMark({ mark, size = 26, className }: Props): JSX.Element {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 32 32',
    className,
    'aria-hidden': true as const,
    focusable: 'false' as const,
  };

  switch (mark) {
    // Kim cương nhiều mặt cắt — vảy rồng cách điệu, đặc và có trọng lượng.
    case 'kim-cuong':
      return (
        <svg {...common}>
          <path d="M16 1.5 30.5 16 16 30.5 1.5 16Z" fill="var(--brand)" />
          <path d="M16 1.5 30.5 16 16 16Z" fill="var(--brand-strong)" />
          <path d="M16 16h14.5L16 30.5Z" fill="var(--brand-dim)" />
          <path d="M16 8.5 23.5 16 16 23.5 8.5 16Z" fill="var(--brand-contrast)" opacity="0.22" />
        </svg>
      );

    // Lá ngọc — hai cung tròn gặp nhau, không một góc nhọn nào.
    case 'la-ngoc':
      return (
        <svg {...common}>
          <path d="M27 5C27 17.15 20.73 27 12.5 27 8.36 27 5 23.64 5 19.5 5 9.28 14.5 5 27 5Z" fill="var(--brand)" />
          <path
            d="M24 8C17 11.5 12.4 17.2 10 24.5"
            stroke="var(--brand-contrast)"
            strokeWidth="1.8"
            strokeLinecap="round"
            fill="none"
            opacity="0.55"
          />
        </svg>
      );

    // Tia chớp — chỉ góc nhọn, cạnh thẳng, không bo một điểm nào.
    case 'tia-chop':
      return (
        <svg {...common}>
          <path d="M18.5 1 5 18h8l-2.5 13L27 13h-9l2.5-12Z" fill="var(--brand)" />
          <path d="M18.5 1 5 18h8Z" fill="var(--accent)" />
        </svg>
      );

    // Khối vuông lồng nhau — nét mảnh, tuyệt đối cân, không tô đặc.
    case 'khoi-vuong':
    default:
      return (
        <svg {...common}>
          <rect x="2.5" y="2.5" width="27" height="27" stroke="var(--brand-dim)" strokeWidth="1.25" fill="none" />
          <rect x="8.5" y="8.5" width="15" height="15" stroke="var(--brand)" strokeWidth="1.25" fill="none" />
          <rect x="14" y="14" width="4" height="4" fill="var(--brand-strong)" />
        </svg>
      );
  }
}
